// StaleDataNotice — a point-of-use "you're offline, this data is N old" cue.
//
// R-staleness (offline audit 2026-06-30, P2.3): the persisted React-Query cache
// (≤24h) is shown with NO age cue, and `refetchOnMount:'always'` is paused
// offline because queries run at the default `networkMode:'online'`. For
// compliance data — chemical registration numbers, PHIs — a silently-stale
// offline read is a real-world risk (an operator trusts a withholding period
// that changed since the device last synced). Surface the age where the data is
// read, only while offline (online, the query refetches, so it isn't stale).
//
// Pass the query's `dataUpdatedAt` (ms epoch, 0 until first load). Renders
// nothing online or before any data exists.
import { useSyncExternalStore } from 'react'
import { CloudOff } from 'lucide-react'
import clsx from 'clsx'

function subscribeOnline(cb: () => void): () => void {
  window.addEventListener('online', cb)
  window.addEventListener('offline', cb)
  return () => {
    window.removeEventListener('online', cb)
    window.removeEventListener('offline', cb)
  }
}

// Online-status hook — subscribes to the browser's online/offline events.
// (navigator.onLine can lie on a dead link, but for a "showing cached data" cue
// a false-negative just hides an honest notice; it never asserts fresh data.)
// Internal for now; export it if another surface needs the same subscription.
function useIsOnline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    () => (typeof navigator !== 'undefined' ? navigator.onLine : true),
    () => true, // SSR / no-navigator: assume online (render nothing)
  )
}

function humanAge(ms: number): string {
  const mins = Math.floor(ms / 60_000)
  if (mins < 1)  return 'moments'
  if (mins < 60) return `${mins} min`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs}h`
  return `${Math.floor(hrs / 24)}d`
}

export function StaleDataNotice({
  dataUpdatedAt,
  now = Date.now(),
  className,
}: {
  dataUpdatedAt: number
  /** Injectable clock for tests; defaults to Date.now() at render. */
  now?: number
  className?: string
}) {
  const online = useIsOnline()
  if (online || !dataUpdatedAt) return null
  return (
    <div
      role="status"
      className={clsx(
        'flex items-center gap-1.5 text-[11px] text-amber-400',
        className,
      )}
    >
      <CloudOff size={12} className="shrink-0" />
      <span>Offline — showing data from {humanAge(now - dataUpdatedAt)} ago</span>
    </div>
  )
}
